import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronLeft, Mail, MapPin, Heart, Users } from 'lucide-react';

const Contact = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-32 md:pb-8">
      {/* Hero section - Landing page style with images */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f0fdfa] via-white to-[#eff6ff]">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors z-10 shadow-sm"
          data-testid="contact-back-btn"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <Link to="/prelanzamiento" className="absolute top-4 left-16 z-10" onClick={() => window.scrollTo(0,0)}>
          <img src="/utentik-logo.png" alt="utentik" className="h-5 brightness-0 invert opacity-80" />
        </Link>

        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-16 md:pt-20 pb-12 md:pb-16">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Text */}
            <div className="order-2 md:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6">
                <MapPin className="w-3 h-3" />
                México
              </div>
              <h1
                className="text-3xl md:text-5xl font-bold text-gray-800 leading-[1.1] mb-5 tracking-tight"
                data-testid="contact-slogan"
              >
                Vive, crea y comparte{' '}<br className="hidden sm:block" />
                <span className="text-primary">tu pasión</span>
              </h1>
              <p className="text-base md:text-lg text-gray-400 leading-relaxed max-w-lg">
                Conectamos personas con experiencias auténticas en todo México. Desde talleres de cerámica hasta caminatas en la montaña.
              </p>
              <div className="flex items-center gap-4 mt-6" data-testid="contact-social">
                <a
                  href="https://www.instagram.com/utentikcom/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-xl bg-white border border-gray-200 hover:border-primary/30 hover:bg-primary/5 transition-all"
                  data-testid="contact-instagram"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" />
                    <circle cx="12" cy="12" r="5" />
                    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@utentikcom"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-xl bg-white border border-gray-200 hover:border-primary/30 hover:bg-primary/5 transition-all"
                  data-testid="contact-tiktok"
                  aria-label="TikTok"
                >
                  <svg className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/utentik"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-xl bg-white border border-gray-200 hover:border-primary/30 hover:bg-primary/5 transition-all"
                  data-testid="contact-facebook"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Image grid - same style as landing */}
            <div className="order-1 md:order-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-3">
                  <div className="rounded-2xl overflow-hidden aspect-[3/4]">
                    <img
                      src="https://images.unsplash.com/photo-1544928147-79a2dbc1f389?w=600&q=80"
                      alt="Taller creativo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden aspect-square">
                    <img
                      src="https://images.unsplash.com/photo-1683553613848-7bf405f7082e?w=400&q=80"
                      alt="Clase de cocina"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="space-y-3 pt-8">
                  <div className="rounded-2xl overflow-hidden aspect-square">
                    <img
                      src="https://images.unsplash.com/photo-1560831340-b9679dc9e9f0?w=400&q=80"
                      alt="Evento grupal"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden aspect-[3/4]">
                    <img
                      src="https://images.unsplash.com/photo-1683105693841-8c81235be472?w=600&q=80"
                      alt="Experiencia gastronómica"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 pt-10">
        {/* Cards grid */}
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          {/* Propósito */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="contact-proposito">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Heart className="w-5 h-5 text-primary" />
            </div>
            <h2 className="font-bold text-gray-800 mb-2">
              {t('contact.purpose')}
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed">
              {t('contact.purposeText')}
            </p>
          </div>

          {/* Buscamos */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="contact-buscamos">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <h2 className="font-bold text-gray-800 mb-2">
              {t('contact.seeking')}
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed">
              {t('contact.seekingText')}
            </p>
          </div>
        </div>

        {/* Location badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm text-gray-500">
            <MapPin className="w-4 h-4 text-primary" />
            Experiencias en todo México
          </div>
        </div>

        {/* Contact emails */}
        <div className="space-y-3" data-testid="contact-emails">
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-start gap-4 hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-2">
                {t('contact.generalDesc')}
              </p>
              <div className="flex items-center gap-3">
                <a href="https://www.instagram.com/utentikcom/" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold text-sm hover:underline" data-testid="contact-general-instagram">
                  Instagram
                </a>
                <span className="text-gray-300">·</span>
                <a href="https://www.tiktok.com/@utentikcom" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold text-sm hover:underline" data-testid="contact-general-tiktok">
                  TikTok
                </a>
                <span className="text-gray-300">·</span>
                <a href="https://www.facebook.com/utentik" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold text-sm hover:underline" data-testid="contact-general-facebook">
                  Facebook
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-start gap-4 hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">
                {t('contact.supportDesc')}
              </p>
              <a
                href="mailto:soporte@utentik.com"
                className="text-primary font-semibold text-sm hover:underline"
                data-testid="contact-email-support"
              >
                soporte@utentik.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
